/** In-memory store for admin training mode (never hits real API). */

function uid(prefix = "trn") {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function createEmptyTrainingStore() {
  return {
    directions: [],
    branches: [],
    /** Groups = specializations in API */
    specializations: [],
    scheduleSlots: {},
    plans: [],
    clients: [],
    memberships: [],
    sessions: [],
    /** sessionId -> journal rows */
    journals: {},
    registrationLinks: [
      {
        id: uid("link"),
        token: "demo-train-link",
        name: "Ссылка для родителей (демо)",
        is_active: true,
        url: "https://bay-sport.ru/r/demo-train-link",
        public_url: "https://bay-sport.ru/r/demo-train-link",
      },
    ],
    events: [],
    meta: {
      attendanceMarked: false,
      inviteSeen: false,
      linkCopied: false,
      visited: {
        overview: false,
        chat: false,
        team: false,
        finance: false,
        settings: false,
      },
    },
  };
}

export function createTrainingStoreController(initial = createEmptyTrainingStore()) {
  let state = initial;
  const listeners = new Set();

  const emit = (type, payload) => {
    const event = { type, payload, at: Date.now() };
    state = {
      ...state,
      events: [...state.events.slice(-40), event],
    };
    listeners.forEach((fn) => {
      try {
        fn(event, state);
      } catch {
        /* ignore */
      }
    });
  };

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    reset() {
      state = createEmptyTrainingStore();
      emit("reset", null);
    },
    snapshot() {
      return state;
    },
    predicates: {
      hasDirection: () =>
        (state.directions || []).some((d) => d.is_active !== false),
      hasVenue: () =>
        (state.branches || []).some((b) => b.is_active !== false),
      hasGroup: () =>
        (state.specializations || []).some((g) => g.is_active !== false),
      hasSchedule: () => {
        const slots = Object.values(state.scheduleSlots || {}).flat();
        return slots.length > 0 || (state.sessions || []).length > 0;
      },
      hasPlan: () => (state.plans || []).some((p) => p.is_active !== false),
      hasClient: () => (state.clients || []).length > 0,
      hasIssuedMembership: () =>
        (state.memberships || []).some((m) => m.status !== "cancelled"),
      hasAttendance: () => Boolean(state.meta.attendanceMarked),
      hasParentInvite: () =>
        Boolean(state.meta.inviteSeen || state.meta.linkCopied),
      hasVisitedOverview: () => Boolean(state.meta.visited?.overview),
      hasVisitedChat: () => Boolean(state.meta.visited?.chat),
      hasVisitedTeam: () => Boolean(state.meta.visited?.team),
      hasVisitedFinance: () => Boolean(state.meta.visited?.finance),
      hasVisitedSettings: () => Boolean(state.meta.visited?.settings),
    },
    actions: {
      addDirection(data = {}) {
        const row = {
          id: uid("dir"),
          name: data.name || "Новое направление",
          is_active: true,
          ...data,
        };
        state = { ...state, directions: [...state.directions, row] };
        emit("direction_created", row);
        return row;
      },
      addBranch(data = {}) {
        const row = {
          id: uid("br"),
          name: data.name || "Новый зал",
          address: data.address || "",
          is_active: true,
          ...data,
        };
        state = { ...state, branches: [...state.branches, row] };
        emit("branch_created", row);
        return row;
      },
      addGroup(data = {}) {
        const direction =
          state.directions.find((d) => d.id === data.direction_id) ||
          state.directions[0];
        const branch =
          state.branches.find((b) => b.id === data.branch_id) ||
          state.branches[0];
        const row = {
          id: uid("grp"),
          name: data.name || "Новая группа",
          is_active: true,
          direction_id: direction?.id,
          branch_id: branch?.id,
          sport_direction: direction
            ? { id: direction.id, name: direction.name }
            : null,
          branch: branch ? { id: branch.id, name: branch.name } : null,
          ...data,
        };
        state = {
          ...state,
          specializations: [...state.specializations, row],
          scheduleSlots: { ...state.scheduleSlots, [row.id]: [] },
        };
        emit("group_created", row);
        return row;
      },
      saveScheduleSlots(groupId, slots) {
        const list = Array.isArray(slots) ? slots : slots?.slots || [];
        const normalized = list.map((s) => ({
          id: s.id || uid("slot"),
          weekday: s.weekday ?? s.day_of_week ?? 1,
          start_time: s.start_time || "18:00",
          end_time: s.end_time || "19:00",
          ...s,
        }));
        state = {
          ...state,
          scheduleSlots: { ...state.scheduleSlots, [groupId]: normalized },
        };
        // Ensure at least one session exists for journal lesson
        if (normalized.length && state.sessions.length === 0) {
          const group =
            state.specializations.find((g) => g.id === groupId) ||
            state.specializations[0];
          const session = {
            id: uid("sess"),
            group_id: groupId,
            specialization_id: groupId,
            specialization: group,
            group,
            start_datetime: `${todayISO()}T18:00:00`,
            end_datetime: `${todayISO()}T19:00:00`,
            status: "scheduled",
            title: group?.name || "Тренировка",
            needs_attendance: true,
          };
          state = {
            ...state,
            sessions: [session],
            journals: {
              ...state.journals,
              [session.id]: { session, rows: [] },
            },
          };
        }
        emit("schedule_saved", { groupId, slots: normalized });
        return normalized;
      },
      addPlan(data = {}) {
        const row = {
          id: uid("plan"),
          name: data.name || "Абонемент 8 занятий",
          plan_type: data.plan_type || "visits",
          visits_total: data.visits_total ?? data.visits ?? 8,
          duration_days: data.duration_days ?? null,
          price_rub: data.price_rub ?? data.price ?? 4000,
          is_active: true,
          group_ids: data.group_ids || state.specializations.map((g) => g.id),
          ...data,
        };
        state = { ...state, plans: [...state.plans, row] };
        emit("plan_created", row);
        return row;
      },
      addClient(data = {}) {
        const group = state.specializations[0];
        const row = {
          id: uid("cli"),
          full_name: data.full_name || data.name || "Иван Демо",
          email: data.email || null,
          phone: data.phone || "+79000000000",
          role: "client",
          is_archived: false,
          sport: {
            registration_status: "approved",
            is_archived: false,
            group_ids: group ? [group.id] : [],
          },
          ...data,
        };
        state = { ...state, clients: [...state.clients, row] };
        if (group) {
          // keep group members implicit via client.sport.group_ids
        }
        // attach to journal rows for existing sessions
        const journals = { ...state.journals };
        for (const sess of state.sessions) {
          const j = journals[sess.id] || { session: sess, rows: [] };
          journals[sess.id] = {
            ...j,
            rows: [
              ...j.rows,
              {
                patient_id: row.id,
                patient: row,
                status: "unknown",
                full_name: row.full_name,
              },
            ],
          };
        }
        state = { ...state, journals };
        emit("client_created", row);
        return row;
      },
      issueMembership(data = {}) {
        const client =
          state.clients.find((c) => c.id === data.patient_id) ||
          state.clients[0];
        const plan =
          state.plans.find((p) => p.id === data.plan_id) || state.plans[0];
        if (!client || !plan) {
          const err = new Error("Сначала создайте ученика и тариф");
          err.response = { data: { error: err.message } };
          throw err;
        }
        const row = {
          id: uid("mem"),
          patient_id: client.id,
          plan_id: plan.id,
          status: data.status || "active",
          visits_remaining:
            data.visits_remaining ??
            plan.visits_total ??
            8,
          starts_on: data.starts_on || todayISO(),
          ends_on: data.ends_on || addDaysISO(30),
          patient: client,
          plan,
          client,
          ...data,
        };
        state = { ...state, memberships: [...state.memberships, row] };
        emit("membership_issued", row);
        return row;
      },
      markAttendance(sessionId, payload = {}) {
        const sess =
          state.sessions.find((s) => s.id === sessionId) || state.sessions[0];
        if (!sess) {
          const err = new Error("Нет занятия для отметки");
          err.response = { data: { error: err.message } };
          throw err;
        }
        const j = state.journals[sess.id] || {
          session: sess,
          rows: state.clients.map((c) => ({
            patient_id: c.id,
            patient: c,
            status: "unknown",
            full_name: c.full_name,
          })),
        };
        const marks = payload.marks || payload.attendance || [];
        let rows = j.rows.map((r) => ({ ...r }));
        if (Array.isArray(marks) && marks.length) {
          for (const m of marks) {
            const pid = m.patient_id || m.client_id;
            const idx = rows.findIndex((r) => r.patient_id === pid);
            const status = m.status || (m.present ? "present" : "absent");
            if (idx >= 0) rows[idx] = { ...rows[idx], status };
            else
              rows.push({
                patient_id: pid,
                status,
                full_name: m.full_name || "Ученик",
              });
          }
        } else if (rows.length) {
          // Single-patient mark without marks array: leave others, mark first present if empty payload
          if (!payload.patient_id) {
            rows = rows.map((r) => ({ ...r, status: "present" }));
          }
        } else if (state.clients[0]) {
          rows = [
            {
              patient_id: state.clients[0].id,
              patient: state.clients[0],
              status: "present",
              full_name: state.clients[0].full_name,
            },
          ];
        }
        // burn a visit on memberships
        const memberships = state.memberships.map((m) => {
          if (m.status !== "active") return m;
          if (m.visits_remaining == null) return m;
          return {
            ...m,
            visits_remaining: Math.max(0, Number(m.visits_remaining) - 1),
          };
        });
        state = {
          ...state,
          memberships,
          journals: { ...state.journals, [sess.id]: { session: sess, rows } },
          meta: { ...state.meta, attendanceMarked: true },
        };
        emit("attendance_marked", { sessionId: sess.id, rows });
        return { session: sess, rows, result: 0 };
      },
      markInviteSeen() {
        state = {
          ...state,
          meta: { ...state.meta, inviteSeen: true },
        };
        emit("invite_seen", null);
      },
      markLinkCopied() {
        state = {
          ...state,
          meta: { ...state.meta, linkCopied: true, inviteSeen: true },
        };
        emit("link_copied", null);
      },
      markTourVisit(sectionId) {
        if (!sectionId) return;
        state = {
          ...state,
          meta: {
            ...state.meta,
            visited: {
              ...(state.meta.visited || {}),
              [sectionId]: true,
            },
          },
        };
        emit("tour_visit", sectionId);
      },
      updateGroup(id, data = {}) {
        const specializations = state.specializations.map((g) =>
          g.id === id ? { ...g, ...data } : g,
        );
        state = { ...state, specializations };
        const row = specializations.find((g) => g.id === id);
        emit("group_updated", row);
        return row;
      },
      updatePlan(id, data = {}) {
        const plans = state.plans.map((p) =>
          p.id === id ? { ...p, ...data } : p,
        );
        state = { ...state, plans };
        const row = plans.find((p) => p.id === id);
        emit("plan_updated", row);
        return row;
      },
      updateClient(id, data = {}) {
        const clients = state.clients.map((c) =>
          c.id === id ? { ...c, ...data } : c,
        );
        state = { ...state, clients };
        const row = clients.find((c) => c.id === id);
        emit("client_updated", row);
        return row;
      },
      addRegistrationLink(data = {}) {
        const link = {
          id: uid("link"),
          token: data.token || `demo-${Date.now()}`,
          name: data.name || "Ссылка регистрации",
          is_active: true,
          url:
            data.url ||
            data.public_url ||
            `https://bay-sport.ru/r/demo-${Date.now()}`,
          public_url:
            data.public_url ||
            data.url ||
            `https://bay-sport.ru/r/demo-${Date.now()}`,
          ...data,
        };
        state = {
          ...state,
          registrationLinks: [...state.registrationLinks, link],
          meta: { ...state.meta, inviteSeen: true },
        };
        emit("registration_link_created", link);
        return link;
      },
      addGroupMember(groupId, patientId) {
        const clients = state.clients.map((c) => {
          if (c.id !== patientId) return c;
          const ids = new Set(c.sport?.group_ids || []);
          ids.add(groupId);
          return { ...c, sport: { ...c.sport, group_ids: [...ids] } };
        });
        state = { ...state, clients };
        emit("group_member_added", { groupId, patientId });
      },
      ensureSession() {
        if (state.sessions.length) return state.sessions[0];
        const group = state.specializations[0];
        if (!group) return null;
        const session = {
          id: uid("sess"),
          group_id: group.id,
          specialization_id: group.id,
          specialization: group,
          start_datetime: `${todayISO()}T18:00:00`,
          end_datetime: `${todayISO()}T19:00:00`,
          status: "scheduled",
          title: group.name,
          group,
          needs_attendance: true,
        };
        const rows = state.clients.map((c) => ({
          patient_id: c.id,
          patient: c,
          status: "unknown",
          full_name: c.full_name,
        }));
        state = {
          ...state,
          sessions: [session],
          journals: { ...state.journals, [session.id]: { session, rows } },
        };
        return session;
      },
    },
  };
}
